import { useEffect, useCallback, DependencyList, useReducer } from 'react'
import { mergeLeft } from 'merge-left-utils'

export interface useFetchMorePaginationArgs {
  loading?: boolean
  page?: number
  pageVariable?: string
  variables?: any
  fetchMore: Function
  updaterFunction: Function
  usePager?: boolean
}

interface PaginationState {
  offset: number
  hasMore: boolean
  loadingMore: boolean
}

enum PaginationReducerActions {
  START_LOADING = 'start',
  STOP_LOADING = 'stop',
  UPDATE_OFFSET = 'updateOffset',
  UPDATE_HAS_MORE = 'updateHasMore',
  RESET = 'reset'
}

interface PaginationReducerAction {
  type: PaginationReducerActions
  payload?: any
}

const makeDefaultReducerState: (page: number) => PaginationState = (
  page = 1
) => ({
  offset: page,
  hasMore: true,
  loadingMore: false
})

const stateReducer = (
  state: PaginationState,
  action: PaginationReducerAction
) => {
  switch (action.type) {
  case PaginationReducerActions.START_LOADING:
    return mergeLeft(state, { loadingMore: true })
  case PaginationReducerActions.STOP_LOADING:
    return mergeLeft(state, { loadingMore: false })
  case PaginationReducerActions.UPDATE_OFFSET:
    return mergeLeft(state, { offset: action.payload })
  case PaginationReducerActions.UPDATE_HAS_MORE:
    return mergeLeft(state, { hasMore: action.payload })
  case PaginationReducerActions.RESET:
    return makeDefaultReducerState(1)
  default:
    return state
  }
}

export default function useFetchMorePagination (
  {
    loading,
    fetchMore,
    page = 1,
    pageVariable = 'page',
    usePager = false,
    variables = {},
    updaterFunction
  }: useFetchMorePaginationArgs,
  dependenciesArray: DependencyList = []
) {
  const [ state, dispatch ] = useReducer(
    stateReducer,
    makeDefaultReducerState(page)
  )

  useEffect(() => {
    dispatch({ type: PaginationReducerActions.RESET })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependenciesArray)

  const fetchNextPage = useCallback(() => {
    if (!loading && !state.loadingMore) {
      const newOffset = state.offset + 1

      dispatch({ type: PaginationReducerActions.START_LOADING })

      fetchMore({
        variables: {
          ...variables,
          [pageVariable]: usePager ? newOffset : {
            page: newOffset
          }
        },
        updateQuery (previousResult = {}, { fetchMoreResult = {} }) {
          const { hasMore = false, result } = updaterFunction(
            previousResult,
            fetchMoreResult
          )
          dispatch({
            type: PaginationReducerActions.UPDATE_OFFSET,
            payload: newOffset
          })
          dispatch({
            type: PaginationReducerActions.UPDATE_HAS_MORE,
            payload: hasMore
          })

          return result
        }
      })
        .then(() => {
          dispatch({ type: PaginationReducerActions.STOP_LOADING })
        })
        .catch(() => {
          dispatch({ type: PaginationReducerActions.STOP_LOADING })
          dispatch({
            type: PaginationReducerActions.UPDATE_HAS_MORE,
            payload: false
          })
        })
    }
  }, [
    fetchMore,
    loading,
    pageVariable,
    state.loadingMore,
    state.offset,
    updaterFunction,
    variables,
    usePager
  ])

  return {
    currentPage: state.offset,
    hasMore: state.hasMore,
    loadingMore: state.loadingMore,
    fetchNextPage
  }
}
